import { FC, MutableRefObject, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import { MoreVert } from "@material-ui/icons";
import { Popover, Tooltip } from "antd";
import {
  FORECAST_TRUMPING_FOLDER_NAME,
  MCDANIEL_SEARCH_FOLDER_NAME,
  NOVI_FOLDER_NAME,
  WORKING_FORECAST_FOLDER_NAME
} from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useColumns } from "hooks";

import { getColumnSets } from "api/column";

import { IColumnSet, IColumns } from "models/columns";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import { useGroupModeState } from "components/filter/production/hooks/useGroupModeController";
import { useGroupByState } from "components/groupBy/group-by.context";
import SegmentedToggle from "components/shared/SegmentedToggle";

import { EntityKind } from "../../models/entityKind";
import { BaseDropdown } from "../base";
import CopyButton from "./CopyButton";
import DataSettingsButton from "./DataSettingsButton";
import "./DataTable.scss";
import DeleteButton from "./DeleteButton";
import ExportButton from "./ExportButton";
import MonthlyCopyButton from "./MonthlyCopyButton";
import MonthlyExportButton from "./MonthlyExportButton";
import SaveButton from "./SaveButton";
import { DataTableViewType, setDataTableView, useTable } from "./context";
import { fadeInOnSwitch, useSwitched } from "./monthlyFade";

export type ForecastSource =
  | typeof FORECAST_TRUMPING_FOLDER_NAME
  | typeof MCDANIEL_SEARCH_FOLDER_NAME
  | typeof NOVI_FOLDER_NAME
  | typeof WORKING_FORECAST_FOLDER_NAME
  | string;

export interface DataSourceSetting {
  pdenSource: number;
  forecastSource: ForecastSource;
  rescat: string;
  startDate?: Maybe<Date>;
  endDate?: Maybe<Date>;
}

type DataTableActionsT = {
  selectedProperties: string[];
  onSelectedColumnSetChanged: (columnSet: IColumnSet) => void;
  dataSources: DataSourceSetting;
  initialState?: { selectedProperties: string[]; pdenSource: PdenSourceEnum };
  onSourcesChanged: (source: DataSourceSetting) => void;
  rows: Record<string, string>[];
  columns: IColumns[];
  dropdownContainerRef?: MutableRefObject<HTMLDivElement>;
};

const DataTableActions: FC<DataTableActionsT> = ({
  selectedProperties,
  onSelectedColumnSetChanged,
  dataSources,
  initialState,
  onSourcesChanged,
  rows,
  columns,
  dropdownContainerRef
}) => {
  // context
  const {
    selectedPdenSource,
    selectedForecastFolder,
    selectedRescat,
    entityKind,
    selectedStartDate,
    selectedEndDate
  } = useGroupByState();

  // state from store
  const [{ overflowindex, dataTableView, monthlyView }, tableDispatch] = useTable();
  const { isGroupMode } = useGroupModeState(entityKind);

  // state
  const [columnSets, setColumnSets] = useState<IColumnSet[]>([]);
  const [selectedColumnSet, setSelectedColumnSet] = useState<IColumnSet>();
  const [overflowVisible, setOverflowVisible] = useState(false);
  const [fieldGroups, setFieldGroups] = useState([]);

  const resourcePlays = useSelector((state: RootState) => state.filter.currentPlayList);

  const allColumns = useColumns();

  // handlers
  function handleColumnSelection(columnSet) {
    setSelectedColumnSet(columnSet);
    onSelectedColumnSetChanged && onSelectedColumnSetChanged(columnSet);
  }

  const { data, isFetching } = useQuery(
    "getColumnSets",
    () => getColumnSets(entityKind),
    {
      enabled: true
    }
  );

  useEffect(() => {
    if (!data?.data) return;
    setColumnSets(data.data);
  }, [data]);

  useEffect(() => {
    // Match selectedProperties with allColumns and extract groups
    const groups = selectedProperties
      ?.filter((property) => property !== "Header.EntityName")
      .map((property) => {
        const matchedColumn = allColumns.find((col) => col.property === property);
        return matchedColumn?.group;
      });

    const uniqueGroups = Array.from(new Set(groups));
    setFieldGroups(uniqueGroups);
  }, [selectedProperties, allColumns]);

  useEffect(() => {
    onSourcesChanged &&
      onSourcesChanged({
        forecastSource: selectedForecastFolder,
        pdenSource: selectedPdenSource,
        rescat: selectedRescat,
        startDate: selectedStartDate,
        endDate: selectedEndDate
      });
  }, [
    selectedPdenSource,
    selectedForecastFolder,
    selectedRescat,
    selectedStartDate,
    selectedEndDate
  ]);

  // REQ-UI-TOG-002: Groups/Wells toggle — only shown when group mode is active and not Facility
  const groupToggle =
    !monthlyView && isGroupMode && entityKind !== EntityKind.Facility ? (
      <Tooltip
        title="Switch between group-aggregated rows and individual well rows"
        mouseEnterDelay={0.6}>
        <SegmentedToggle
          options={[
            { label: "Groups", value: "group" as DataTableViewType },
            { label: "Wells", value: "well" as DataTableViewType }
          ]}
          value={dataTableView}
          onChange={(v) => setDataTableView(tableDispatch, v as DataTableViewType)}
        />
      </Tooltip>
    ) : null;

  // REQ-UI-TOG-002: Action buttons that participate in the overflow system.
  // Visual order: Save, Delete, DataSettings, Export, Copy
  const actionButtons = [
    <SaveButton
      key="Save"
      selectedColumnSet={selectedColumnSet}
      selectedProperties={selectedProperties}
    />,
    <DeleteButton
      key="Delete"
      setSelectedColumnSet={setSelectedColumnSet}
      selectedColumnSet={selectedColumnSet}
    />,
    ...(entityKind === EntityKind.Well
      ? [<DataSettingsButton key="DataSource" initialState={initialState} />]
      : []),
    <ExportButton
      key="Export"
      selectedProperties={selectedProperties}
      dataSources={dataSources}
      resourcePlays={resourcePlays}
      rowCount={rows.length}
      fieldGroups={fieldGroups}
    />,
    <CopyButton
      key="Copy"
      rows={rows}
      columns={columns}
      selectedProperties={selectedProperties}
      entityKind={entityKind}
    />
  ];

  const fade = useSwitched(monthlyView);

  const totalButtons = actionButtons.length;
  const hasOverflow = overflowindex < totalButtons;
  const visibleCount = hasOverflow ? Math.max(0, overflowindex - 1) : totalButtons;

  function getVisibleButtons() {
    return actionButtons.slice(0, visibleCount);
  }

  function getHiddenButtons() {
    if (!hasOverflow) return null;
    return actionButtons.slice(visibleCount);
  }

  // Monthly production has its own fixed data source and none of the Data columns, so
  // only Export and Copy remain, with no overflow menu. The dropdown container stays
  // mounted, empty, so the overflow measurement keeps observing the same element.
  return (
    <ActionsContainer>
      <DropDownContainer ref={dropdownContainerRef}>
        {!monthlyView && (
          <DropDownContents $fade={fade}>
            {groupToggle && <GroupToggleWrapper>{groupToggle}</GroupToggleWrapper>}
            <ColumnSetDropdownWrapper>
              <BaseDropdown
                value={selectedColumnSet}
                labelKey={"title"}
                onChange={handleColumnSelection}
                options={columnSets}
                inlineLabel={selectedColumnSet ? undefined : "Saved columns"}
                isLoading={isFetching}
              />
            </ColumnSetDropdownWrapper>
          </DropDownContents>
        )}
      </DropDownContainer>
      <ButtonContainer key={monthlyView ? "monthly" : "data"} $fade={fade}>
        {monthlyView ? (
          <>
            <MonthlyExportButton />
            <MonthlyCopyButton />
          </>
        ) : (
          <>
            {getVisibleButtons()}
            {hasOverflow && (
              <Popover
                arrowPointAtCenter
                content={<OverflowWrapper>{getHiddenButtons()}</OverflowWrapper>}
                placement="bottomRight"
                trigger="click"
                open={overflowVisible}
                onOpenChange={(v) => setOverflowVisible(v)}>
                <Tooltip title="More" mouseEnterDelay={0.6}>
                  <ActionButton>
                    <MoreVert fontSize="large" />
                  </ActionButton>
                </Tooltip>
              </Popover>
            )}
          </>
        )}
      </ButtonContainer>
    </ActionsContainer>
  );
};
export default DataTableActions;

const ButtonContainer = styled.div<{ $fade: boolean }>`
  ${(p) => p.$fade && fadeInOnSwitch}
  display: flex;
  flex-shrink: 0;
  flex-grow: 1;
  flex-basis: 0;
  min-width: 36px;
  align-items: center;
  gap: 4px;
`;

const DropDownContents = styled.div<{ $fade: boolean }>`
  ${(p) => p.$fade && fadeInOnSwitch}
  display: flex;
  min-width: 0;
`;

const DropDownContainer = styled.div`
  display: flex;
  flex-shrink: 1;
  flex-grow: 0;
  min-width: 0;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const ColumnSetDropdownWrapper = styled.div`
  max-width: 165px;
  min-width: 20px;
  overflow: hidden;

  & .BaseDropdown {
    max-width: 100%;
  }
`;
const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  display: grid;
  grid-template-columns: 36px max-content;
  gap: 6px;
  align-items: center;
  justify-items: center;
  background: transparent;
  border: none;
  color: #a2aaad;
  font-weight: 500;
  padding: 0;
  margin: 0;
  transition: color 0.15s ease-out;
  overflow: hidden;
  pointer-events: all;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${(props) => (props.danger ? "var(--color-danger)" : "var(--color-primary)")};
  }
`;

const GroupToggleWrapper = styled.div`
  padding-right: 8px;
  display: flex;
  align-items: center;
`;

const OverflowWrapper = styled.div`
  display: grid;
  max-height: 300px;
  border-radius: var(--border-radius);
  padding: 8px 12px;
  margin: -12px -16px;
  overflow: hidden scroll;

  & > button {
    width: auto !important;
  }
`;
